import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Header from '../../Common/Header/Header';
import SuccessPopup from '../../Common/MessagePopup/SuccessPopup';
import FailurePopup from '../../Common/MessagePopup/FailurePopup';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import DatePicker from 'react-datepicker';
import { ChallengeDataType, OptionDataType, PuzzleDataType, TableChallengeDataType } from './ChallengeInterface';
import { GetAllChallenges, GetAllChallengesUnits } from '../../../api/question';
import { ChallengeEnum } from './ChallengeEnum';
import { useNavigate } from 'react-router-dom';
import { setSubMenuActiveTab } from '../../../reducers/tabSplice';
import { getNewAccessToken } from '../../../api/auth';
import { setToken } from '../../../reducers/UserSplice';
import { LoadingDataType, MessageDataType } from '../../Common/Common/CommonInterface';
import { QuestionCategory, QuestionType } from '../Unit-Intro/QuestionEnum';
import { Tooltip } from "react-tooltip";

const Puzzle = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { access_token, refresh_token } = useAppSelector((state) => state.user.token);
  const [viewAllChallenges, setViewAllChallenges] = useState<boolean>(false);

  const [validated, setValidated] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [tableChallengeData, setTableChallengeData] = useState<TableChallengeDataType[]>([]);
  //Challenges Variables
  const [allChallengeData, setAllChallengeData] = useState<PuzzleDataType[]>([]);
  const [editPuzzleData, setEditPuzzleData] = useState<PuzzleDataType>({
    id: "",
    grade: {gradeId: "", gradeName: ""},
    unit: { id: "", name: "" },
    deletedOption: [],
    category: QuestionCategory.puzzle,
    question: "",
    challengeMedia: {
      audioFile: { files: "", blobPath: "", fileExtension: "" }
    },
  })
  const [previewSourceType, setPreviewSourceType] = useState<string | null>(null);
  const [previewSource, setPreviewSource] = useState<string | null>(null);
  const [isVideo, setIsVideo] = useState<boolean>(false);
  const [show, setShow] = useState(false);
  const [confirmationPopupData, setConfirmationPopupData] = useState({
    title: '',
    message: '',
    descriptions: '',
    acceptText: '',
    cancelText: '',
    onAccept: () => { },
    onCancel: () => { },
  });
  const [loading, setLoading] = useState<LoadingDataType>({
    fetchingLoading: false,
    tableLoading: false,
    deleteLoading: false,
    submitLoading: false,
    validationLoading: false,
  });
  const [message, setMessage] = useState<MessageDataType>({
    errorMessage: [],
    successMessage: '',
    failureMessage: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [challengeCount, setChallengeCount] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchtext, setSearchText] = useState<string>('');

  const indexOfLastItem = useMemo(() => currentPage * itemsPerPage, [currentPage, itemsPerPage]);
  const indexOfFirstItem = useMemo(() => indexOfLastItem - itemsPerPage, [indexOfLastItem, itemsPerPage]);



  const handleTableChallengeData = useCallback((challengeData: any) => {
    try {
      const { units, count } = challengeData;
      setChallengeCount(count);

      const formattedData: TableChallengeDataType[] = units.map((unit: any) => {
        return {
          id: unit.id,
          unit: { id: unit.id, name: unit.name },
          lesson: { id: unit.lesson.id, name: unit.lesson.name },
          subject: { id: unit.lesson.subject_id.id, name: unit.lesson.subject_id.name },
          tribe: { id: unit.lesson.tribe_id.id, name: unit.lesson.tribe_id.name },
          status: unit.isActive === "1" ? "PUBLISH" : "UNPUBLISH",
        };
      });
      setTableChallengeData(formattedData);
    } catch (error) {
      console.error('Handle Challenge Data Error:', error);
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, tableLoading: false }));
    }
  }, []);

  const getAuthorizedToken = useCallback(async () => {
    try {
      const newTokenResponse = await getNewAccessToken({ refreshToken: refresh_token });
      const { data, statusCode } = newTokenResponse;
      if (statusCode === 200) {
        dispatch(setToken({
          access_token: data.accessToken,
          refresh_token: data.refreshToken,
        }));
      }
    } catch (error: any) {
      console.error('Something went wrong in getting new access Token :: ', error);
    }
  }, [dispatch, refresh_token]);

  const getAllChallenges = useCallback(async (offset: number, count: number) => {
    setLoading((prevLoading: LoadingDataType) => ({ ...prevLoading, tableLoading: true }));
    try {
      const challengeResponse = await GetAllChallengesUnits(String(offset), String(count), QuestionCategory.puzzle, access_token);
      const { data, statusCode } = challengeResponse;
      if (statusCode === 200) {
        handleTableChallengeData(data);
      } else if (statusCode === 401) {
        getAuthorizedToken();
        setMessage((prevMessage: MessageDataType) => ({
          ...prevMessage,
          failureMessage: 'Authorization token expired. Try again!',
        }));
      }
    } catch (error) {
      console.error('Get All Challenges Error:', error);
    }
  }, [access_token, getAuthorizedToken, handleTableChallengeData]);


  useEffect(() => {
    getAllChallenges(indexOfFirstItem, itemsPerPage);
  }, [getAllChallenges, indexOfFirstItem, itemsPerPage]);


  const handlePagination = useCallback(async (pageNumber: number, direction: string) => {
    if (pageNumber < 1 || pageNumber > Math.ceil(challengeCount / itemsPerPage)) {
      return; // Prevent navigation to invalid pages
    }

    try {
      setLoading((prevLoading) => ({ ...prevLoading, tableLoading: true }));
      if (direction === 'right' && pageNumber <= Math.ceil(challengeCount / itemsPerPage)) {
        setCurrentPage(pageNumber);
      } else if (direction === 'left' && pageNumber >= 1) {
        setCurrentPage(pageNumber);
      }
    } finally {
      setLoading((prevLoading: LoadingDataType) => ({ ...prevLoading, tableLoading: false }));
    }
  }, [challengeCount, itemsPerPage]);

  const handleChallengeData = (challengeData: any) => {
    try {
      setLoading({ ...loading, tableLoading: true });
      // const { questions, count } = challengeData;
      // setChallengeCount(count);

    } catch (error: any) {
      if (error.response && error.response.data && typeof error.response.data === "string") {
        setMessage({
          ...message,
          failureMessage: error.response.data.message
        })
      } else {
        setMessage({
          ...message,
          failureMessage: "Something went wrong. Please try again"
        })
      }
    } finally {
      setLoading({
        ...loading,
        tableLoading: false,
      });
    }
  }

  const viewAllQuestion = useCallback(async (tableChallenge: TableChallengeDataType) => {
    setLoading((prevLoading: LoadingDataType) => ({ ...prevLoading, tableLoading: true }));
    try {
      setViewAllChallenges(true);
      const challengeResponse = await GetAllChallenges(tableChallenge.id, editPuzzleData.grade.gradeId, QuestionCategory.puzzle, '0', String(itemsPerPage), access_token);
      const { data, statusCode } = challengeResponse;
      if (statusCode === 200) {
        handleChallengeData(data)
      } else if (statusCode === 401) {
        getAuthorizedToken();
        setMessage((prevMessage: MessageDataType) => ({
          ...prevMessage,
          failureMessage: 'Authorization token expired. Try again!',
        }));
      }
    } catch (error) {
      console.error('Get All Challenges Error:', error);
    }
  }, [])

  return (
    <div className="dashboard d-flex">
      <Header />
      <div className="rightContainer">
        <div>
          <div className="header-container">
            {message.successMessage && <SuccessPopup message={message.successMessage} />}
            {message.failureMessage && <FailurePopup message={message.failureMessage} />}
            <h2 className="page-title text-center">Challenges</h2>
          </div>
          <div className="pCard">
            <div className="topAction align-items-center">
              <div className="dropdown statusDrop">
                <button className="dropdown-toggle" type="button" id="dropdownMenuButtonStatus" data-bs-toggle="dropdown" aria-expanded="false">
                  Status
                </button>
                <ul className="dropdown-menu" aria-labelledby="dropdownMenuButtonStatus">
                  <li><button className="dropdown-item" name="status">All</button></li>
                  <li><button className="dropdown-item" name="status" value="Publish">Publish</button></li>
                  <li><button className="dropdown-item" name="status" value="Inactive">Inactive</button></li>
                </ul>
              </div>

              <div className="dropdown filter ms-auto">
                <a className="dropdown-toggle" href="#blank" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <img src="./assets/images/filter.svg" alt="Filter" />
                </a>
                <div className="dropdown-menu p-3" onClick={(e) => e.stopPropagation()}>
                  <div className="mb-3">
                    <label htmlFor="startDate" className="form-label">Start Date</label>
                    <DatePicker id="startDate" selected={startDate} onChange={(date) => setStartDate(date)} className="form-control" placeholderText="Select Date" />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="endDate" className="form-label">End Date</label>
                    <DatePicker id="endDate" selected={endDate} onChange={(date) => setEndDate(date)} className="form-control" placeholderText="Select Date" />
                  </div>
                </div>
              </div>
              <div className="searchInput">
                <input type="text" placeholder="Search" name="searchText" value={searchtext} onChange={(e) => setSearchText(e.target.value)} />
              </div>
              <button className="btn add-btn btnPurple w-auto px-3" >+</button>
            </div>

            {loading.tableLoading ? (
              <div className="d-flex align-items-center mt-5">
                <div className="loader mx-auto"></div>
              </div>
            ) : tableChallengeData.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th className="text-center">UNIT</th>
                      <th className="text-center">GRADE</th>
                      <th className="text-center">LESSON</th>
                      <th className="text-center">SUBJECT</th>
                      <th className="text-center">TRIBE</th>
                      <th className="text-center">STATUS</th>
                      <th className='text-center'>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableChallengeData.map((tableChallenge: TableChallengeDataType, index: number) => (
                      <tr key={index}>
                        <td className="text-center">
                          <span data-tooltip-id={`challenge-tooltip-${tableChallenge.id}`} data-tooltip-content={tableChallenge.unit.name} data-tooltip-place="top" >

                            {tableChallenge.unit.name.length > 15 ? tableChallenge.unit.name.substring(0, 15) + "..." : tableChallenge.unit.name}
                          </span>
                          {tableChallenge.unit.name.length > 15 && <Tooltip id={`challenge-tooltip-${tableChallenge.id}`} />}
                        </td>
                        <td className="text-center">{tableChallenge.grade.gradeId === "1" ? "Kinder" : tableChallenge.grade.gradeName}</td>
                        <td className="text-center">
                          <span data-tooltip-id={`challenge-tooltip-${tableChallenge.id}`} data-tooltip-content={tableChallenge.lesson.name} data-tooltip-place="top" >
                            {tableChallenge.lesson.name.length > 15 ? tableChallenge.lesson.name.substring(0, 15) + "..." : tableChallenge.lesson.name}
                          </span>
                          {tableChallenge.lesson.name.length > 15 && <Tooltip id={`challenge-tooltip-${tableChallenge.id}`} />}</td>
                        <td className="text-center">
                          <span data-tooltip-id={`challenge-tooltip-${tableChallenge.id}`} data-tooltip-content={tableChallenge.subject.name} data-tooltip-place="top" >
                            {tableChallenge.subject.name.length > 15 ? tableChallenge.subject.name.substring(0, 15) + "..." : tableChallenge.subject.name}
                          </span>
                          {tableChallenge.subject.name.length > 15 && <Tooltip id={`challenge-tooltip-${tableChallenge.id}`} />}
                        </td>
                        <td className="text-center">
                          <span data-tooltip-id={`challenge-tooltip-${tableChallenge.id}`} data-tooltip-content={tableChallenge.tribe.name} data-tooltip-place="top" >
                            {tableChallenge.tribe.name.length > 15 ? tableChallenge.tribe.name.substring(0, 15) + "..." : tableChallenge.tribe.name}
                          </span>
                          {tableChallenge.tribe.name.length > 15 && <Tooltip id={`challenge-tooltip-${tableChallenge.id}`} />}
                        </td>
                        <td className="text-center">
                          <span className={tableChallenge.status === 'PUBLISHED' ? 'activeStatus' : 'inactiveStatus'}>
                            {tableChallenge.status}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="dropdown dots">
                            <button className="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">...</button>
                            <ul className="dropdown-menu">
                              <li>
                                <button className="dropdown-item" onClick={() => viewAllQuestion(tableChallenge)}>
                                  <img src="assets/images/edit-icon.png" alt="" className="me-2" />
                                  Edit
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item" style={{ cursor: 'pointer' }}>
                                  <img src="assets/images/delete.png" alt="" className="me-2" />
                                  Delete
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (!loading.tableLoading && <p className="text-center mt-5">No Data Found</p>
            )
            }

            <div className="pagination-container">
              <nav className="d-flex justify-content-between">
                <div className="page-length">
                  Showing {Math.min(indexOfFirstItem + 1, challengeCount)} - {Math.min(indexOfLastItem, challengeCount)} of {challengeCount}
                </div>
                <ul className="pagination justify-content-center mx-auto">
                  <li className="page-item">
                    <button className="page-link" aria-label="Previous" onClick={() => handlePagination(currentPage - 1, 'left')}>
                      <span aria-hidden="true"><img src="./assets/images/arrow-left-small.svg" alt="" /></span>
                    </button>
                  </li>
                  <li className="page-item disabled">
                    <span className="page-link">
                      {indexOfFirstItem + 1} of {indexOfLastItem}
                    </span>
                  </li>
                  <li className="page-item">
                    <button className="page-link" aria-label="Next" onClick={() => handlePagination(currentPage + 1, 'right')}>
                      <span aria-hidden="true"><img src="./assets/images/arrow-right-small.svg" alt="" /></span>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Puzzle